import type { FactorType, MFAAction } from '@/api/generated';
import type { FactorStepResponse } from '@/api/generated/models/FactorStepResponse';
import type { MfaFlowResponse } from '@/api/generated/models/MfaFlowResponse';
import type { PushNotificationMetadata } from '@/api/generated/models/PushNotificationMetadata';
import type { ValidateAuthIdResponse } from '@/api/generated/models/ValidateAuthIdResponse';
import type { ValidatePushChallengeResponse } from '@/api/generated/models/ValidatePushChallengeResponse';
import type {
  FactorStep,
  GetAlternativesApiResponse,
  MfaFlowApiResponse,
} from '@/hooks/use-mfa-orchestrator.types';
import {
  escalationService,
  mfaOrchestrationService,
  mfaPushNotificationsService,
  pendingAuthService,
} from '../api-clients';

/**
 * Transform a FactorStepResponse (snake_case) to FactorStep (camelCase)
 */
const transformFactorStep = (
  step: FactorStepResponse | null | undefined
): FactorStep | null | undefined => {
  if (!step) {
    return step;
  }

  return {
    factorType: step.factor_type,
    status: step.status,
    metadata: step.metadata,
  };
};

/**
 * Transform MfaFlowResponse to MfaFlowApiResponse (camelCase)
 */
const transformMfaFlowResponse = (
  response: MfaFlowResponse
): MfaFlowApiResponse => {
  return {
    status: response.status,
    hasAlternative: response.hasAlternative ?? undefined,
    currentStep: transformFactorStep(response.currentStep),
  };
};

export const startMfaFlowFromCookie = async (
  authId?: string
): Promise<MfaFlowApiResponse> => {
  const response = await mfaOrchestrationService.startMfaFlowFromCookie({
    auth_id: authId,
  });

  return transformMfaFlowResponse(response);
};

/**
 * Validate auth_id before starting MFA flow.
 * Checks if pending auth session exists and is not expired.
 * The auth_id is read from the pending_auth_session httponly cookie by the backend.
 */
export interface SignOnApp {
  name: string;
  iconUrl: string | null;
}

export interface AuthIdValidation {
  valid: boolean;
  // Destination app for SP-initiated flows; null for IdP-initiated.
  spApp: SignOnApp | null;
}

export const validateAuthId = async (): Promise<AuthIdValidation> => {
  try {
    const response =
      (await pendingAuthService.validateAuthId()) as ValidateAuthIdResponse;
    const spApp = response.sp_app
      ? {
          name: response.sp_app.name,
          iconUrl: response.sp_app.icon_url ?? null,
        }
      : null;

    return { valid: response.valid, spApp };
  } catch {
    // API errors, network errors, etc. - treat as invalid
    return { valid: false, spApp: null };
  }
};

/**
 * Start escalation authentication flow.
 * Forces fresh authentication regardless of cookies.
 * This should be called when authId is from an escalation flow.
 */
export const startEscalationAuthentication = async (
  authId: string
): Promise<MfaFlowApiResponse> => {
  try {
    const response = await escalationService.startEscalationAuthentication({
      auth_id: authId,
    });

    return transformMfaFlowResponse(response);
  } catch (error) {
    // Network errors, CORS errors, etc. - rethrow to prevent fallback
    if (
      error instanceof TypeError &&
      error.message.includes('Failed to fetch')
    ) {
      throw new Error(
        'Network error: Could not reach escalation endpoint. Check CORS configuration.'
      );
    }

    throw error;
  }
};

export const startMfaFlow = async (
  username: string,
  authId?: string
): Promise<MfaFlowApiResponse> => {
  const response = await mfaOrchestrationService.startMfaFlow({
    username: username,
    auth_id: authId,
  });

  return transformMfaFlowResponse(response);
};

export const startMfaFlowWithExternalSso = async (
  startCode: string,
  authId?: string
): Promise<MfaFlowApiResponse> => {
  const response = await mfaOrchestrationService.startMfaFlow({
    external_sso_start_code: startCode,
    auth_id: authId,
  });

  return transformMfaFlowResponse(response);
};

export const submitMfaResponse = async (
  payload: Record<string, unknown>
): Promise<MfaFlowApiResponse> => {
  const response = await mfaOrchestrationService.submitMfaResponse({
    payload: payload,
  });

  return transformMfaFlowResponse(response);
};

/**
 * Get MFA challenge status (for polling)
 * This goes directly to the push controller, not the orchestrator
 * Uses the session's step_id as the challenge_id
 */
export const getMfaChallengeStatus = async () => {
  const response = await mfaPushNotificationsService.getMfaStatus();

  return response;
};

/**
 * Validate push notification challenge action (approve/reject)
 * This goes directly to the push controller for validation
 * Uses the provided challenge_id from the notification
 */
export const validatePushChallenge = async (
  action: MFAAction,
  challengeId: string
) => {
  const response =
    await mfaPushNotificationsService.validatePushChallengePublic({
      action: action,
      challenge_id: challengeId,
    });

  return response as ValidatePushChallengeResponse;
};

/**
 * Resend push notification challenge
 * This goes directly to the push controller for resending
 * Uses the session's step_id as the challenge_id
 */
export const resendPushChallenge =
  async (): Promise<PushNotificationMetadata> => {
    const response =
      await mfaPushNotificationsService.resendPushChallengePublic();

    if (!response.success || !response.metadata) {
      throw new Error('Push resend was rejected');
    }

    return response.metadata;
  };

/**
 * Get the next alternative method for the flow
 * This goes directly to the orchestrator for getting the next alternative method
 * Uses the session's flow_id as the flow_id
 */
export const getAlternatives =
  async (): Promise<GetAlternativesApiResponse> => {
    const response = await mfaOrchestrationService.getAlternatives();

    return response as GetAlternativesApiResponse;
  };

/**
 * Choose an authenticator for the flow
 * This goes directly to the orchestrator for choosing an authenticator
 * Uses the session's flow_id as the flow_id
 */
export const chooseAuthenticator = async (
  authenticatorType: FactorType
): Promise<MfaFlowApiResponse> => {
  const response = await mfaOrchestrationService.chooseAuthenticator({
    authenticator: authenticatorType,
  });

  return transformMfaFlowResponse(response);
};

// The budget outlives the step metadata a submit response drops.
export const getResendAvailability = async (): Promise<string> => {
  const response = await mfaOrchestrationService.getResendAvailability();

  return response.next_resend_at;
};
